/**
 * The signed-in viewer for the market's public lists (cached for everyone, so they can't say it):
 * their own shop's handle (no cart on one's own cards) and the listings they saved (♡). Asked once
 * per account and shared by every list on screen; a ♡ changes it here at once.
 */
import { useEffect, useState } from "react";
import type { MarketViewer } from "../../shared/market";
import { useAuth } from "../auth/AuthProvider";
import { marketApi } from "./api";

const NOBODY: MarketViewer = { handle: null, favorites: [] };
let forUser: string | null = null;
let value: MarketViewer = NOBODY;
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((f) => f());

function load(userId: string) {
  if (forUser === userId && loading) return;
  forUser = userId;
  value = NOBODY;
  loading = marketApi
    .viewer()
    .then((v) => {
      if (forUser === userId) value = { handle: v.handle, favorites: v.favorites };
    })
    .catch(() => undefined)
    .then(notify);
}

/** Tests: forget the viewer asked before. */
export function resetMarketViewer() {
  forUser = null;
  value = NOBODY;
  loading = null;
}

export function useMarketViewer() {
  const userId = useAuth().user?.id ?? null;
  const [, rerender] = useState(0);
  useEffect(() => {
    const f = () => rerender((n) => n + 1);
    listeners.add(f);
    if (userId) load(userId);
    return () => {
      listeners.delete(f);
    };
  }, [userId]);
  const v = userId && forUser === userId ? value : NOBODY;
  return {
    /** The viewer's own shop, or null. */
    handle: v.handle,
    isMine: (sellerHandle: string) => v.handle !== null && v.handle === sellerHandle,
    isFavorite: (listingId: number) => v.favorites.includes(listingId),
    /** ♡ / undo: shown at once, put back if the server says no. */
    setFavorite: async (listingId: number, on: boolean) => {
      if (!userId || forUser !== userId) return;
      const before = value.favorites;
      value = { ...value, favorites: on ? [listingId, ...before.filter((id) => id !== listingId)] : before.filter((id) => id !== listingId) };
      notify();
      try {
        await marketApi.setFavorite(listingId, on);
      } catch (err) {
        value = { ...value, favorites: before };
        notify();
        throw err;
      }
    },
  };
}
